/**
 * grantGuard — lifetime access grants outrank every billing provider.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Alpha/lifetime testers are granted access with an invite code. The grant
 * is server-verified and stored at `users/{uid}/alpha/grant`; the client
 * marks it on the subscription as `alphaTester: true`. These people NEVER
 * PURCHASED ANYTHING, so a real store provider (RevenueCat) will correctly
 * and permanently answer "no active entitlement" for every one of them.
 *
 * If a provider answer is ever written straight onto `subscription`, the
 * grant is erased locally, the sync funnel pushes that erasure to the
 * cloud, and it propagates to every device the person owns. The people who
 * tested the app for free would silently lose the lifetime access they were
 * promised — on the day billing goes live, which is the worst possible day
 * for a trust failure.
 *
 * THE INVARIANT (SEAM_REGISTRY.md seam 6, REVENUECAT_ROLLOUT.md):
 *
 *     premium = (store entitlement) OR (lifetime grant)
 *
 * A provider may GRANT access. It may never REVOKE a grant.
 *
 * This applies to grants issued in the future exactly as it does to the
 * original 19 — codes are still being issued from the reserve, and some
 * will be redeemed after billing is live (founder rule, 24 Aug 2026).
 *
 * RULE FOR THE RevenueCat IMPLEMENTATION: every value returned by a
 * BillingProvider passes through `preserveGrant()` before it reaches the
 * store. There is no exception, including `restore()` — which today has no
 * call site and will get one.
 */

import type { Subscription } from '../types';

/**
 * The single definition of "this account holds a lifetime grant".
 * Read the FLAG, never the plan string: the plan is what a provider
 * overwrites, the flag is what must survive it.
 */
export function hasLifetimeGrant(sub: Subscription | undefined | null): boolean {
  return sub?.alphaTester === true;
}

/**
 * The canonical shape of a granted subscription. One definition, so a
 * grant applied in 2027 is identical to one applied in 2026.
 * `trialStartedAt` is carried through: it records that a trial was once
 * used and must not be resurrected by the grant.
 */
export function grantSubscription(current?: Subscription, uid?: string): Subscription {
  const grantUid = uid ?? current?.grantUid;
  return {
    plan: 'lifetime',
    trialStartedAt: current?.trialStartedAt,
    alphaTester: true,
    ...(grantUid ? { grantUid } : {}),
  };
}

/**
 * Merge a billing provider's answer onto the current subscription without
 * ever dropping a lifetime grant.
 *
 * The grant is honoured if EITHER side carries it: `current` covers the
 * local holder whose provider says "nothing"; `incoming` covers a grant
 * arriving from the cloud (restoreAlphaGrant / remote snapshot) on a fresh
 * device that has not seen it yet.
 *
 * Returns `incoming` unchanged when no grant is involved, so ordinary
 * paying users are completely unaffected by this guard.
 */
export function preserveGrant(current: Subscription, incoming: Subscription): Subscription {
  if (!hasLifetimeGrant(current) && !hasLifetimeGrant(incoming)) return incoming;
  const grantUid = current.grantUid ?? incoming.grantUid;
  return {
    plan: 'lifetime',
    // Keep whichever trial history exists — a grant must not hand back a
    // fresh 7-day trial to someone who already spent theirs.
    trialStartedAt: current.trialStartedAt ?? incoming.trialStartedAt,
    alphaTester: true,
    ...(grantUid ? { grantUid } : {}),
  };
}

/**
 * THE ONE PLACE a device's copy of a grant is let go (4 Nov 2026, MEASURED
 * on iOS 2.2.2: signing out and into the App Review demo account, and even
 * "Erase this device", left "Alpha Tester — Lifetime access" showing).
 * The grant itself is never touched: it lives in the account
 * (users/{uid}/alpha/grant) and comes back on the next sign-in to it.
 *
 * `uid` given: the signed-in account has DEFINITELY no grant (its grant
 * document was read and does not exist), so a copy confirmed for ANOTHER
 * account is dropped. A legacy copy never tied to an account is kept — it
 * may be a real tester's from before grantUid existed; their own next
 * sign-in ties it (applyAlphaGrant(uid)). `uid` omitted: the device is
 * being erased. A failed read must never call this.
 */
export function withoutDeviceGrant(sub: Subscription, uid?: string): Subscription {
  if (!hasLifetimeGrant(sub)) return sub;
  if (uid !== undefined && (sub.grantUid === undefined || sub.grantUid === uid)) return sub;
  return { plan: 'free', ...(sub.trialStartedAt !== undefined ? { trialStartedAt: sub.trialStartedAt } : {}) };
}
